/**
 * Bundle 79 - Public digital confirmation page.
 *
 * URL: /introductions/confirm/:token
 *
 * Each participant sees ONLY their own on-file contacts (masked). They
 * choose exactly what to share; nothing about the OTHER party is
 * revealed until BOTH have accepted. Then the FE redirects to
 * /connections which surfaces the mutual reveal.
 *
 * No em/en dashes, no eslint-disable, no photo/image alt.
 */
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import { ShieldCheck, Sparkles } from 'lucide-react';

const BASE = process.env.REACT_APP_BACKEND_URL;
const cfmApi = axios.create({ baseURL: `${BASE}/api` });

const IntroductionConfirmPage = () => {
  const { token } = useParams();
  const nav = useNavigate();
  const [ctx, setCtx] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [decision, setDecision] = useState(null);
  const [form, setForm] = useState({
    share_phone: false, share_whatsapp: false,
    share_email: false, share_instagram: false,
    keep_inside_dostination_only: false,
    phone_value: '', whatsapp_value: '',
    email_value: '', instagram_value: '',
  });

  useEffect(() => {
    let mounted = true;
    cfmApi.get(`/introductions/confirm/${token}`)
      .then((r) => { if (mounted) setCtx(r.data); })
      .catch((e) => { if (mounted) toast.error(e?.response?.data?.detail || 'Invalid link'); })
      .finally(() => { if (mounted) setLoading(false); });
    return () => { mounted = false; };
  }, [token]);

  const set = (k) => (v) => setForm((s) => ({ ...s, [k]: typeof v === 'boolean' ? v : v?.target?.value }));

  const send = async (accept) => {
    setSubmitting(true);
    try {
      const body = accept ? { accept: true, ...form } : { accept: false };
      const { data } = await cfmApi.post(`/introductions/confirm/${token}`, body);
      setDecision(data);
      if (data?.connected) {
        toast.success('You are connected. Redirecting to your Connections.');
        setTimeout(() => nav('/connections'), 1600);
      } else if (accept) {
        toast.success('Saved. We will notify you when the other side responds.');
      }
    } catch (e) { toast.error(e?.response?.data?.detail || 'Could not save.'); }
    finally { setSubmitting(false); }
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center text-neutral-500">Loading...</div>;
  if (!ctx) return <div className="min-h-screen flex items-center justify-center text-neutral-500">This link is not valid.</div>;
  if (ctx.already_submitted) return (
    <div className="min-h-screen bg-neutral-50 flex items-center justify-center p-6" data-testid="confirmation-already-submitted">
      <div className="max-w-md text-center">
        <div className="text-primary-900 font-semibold">You have already responded.</div>
        <div className="text-neutral-600 text-sm mt-2">Head to your <a href="/connections" className="text-secondary-800 underline">Connections</a>.</div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-neutral-50" data-testid="confirmation-page">
      <div className="max-w-2xl mx-auto p-4 sm:p-6">
        <div className="flex items-center gap-2 text-secondary-800 text-xs uppercase tracking-widest">
          <Sparkles className="h-3.5 w-3.5" /> {ctx.event?.title}
        </div>
        <h1 className="font-display text-2xl sm:text-3xl font-bold text-primary-900 mt-2">
          If this connection is mutual, what would you like to share?
        </h1>
        <p className="text-primary-700 text-sm mt-2 max-w-xl">
          You are in charge. Nothing is revealed unless the other person also accepts.
        </p>

        <div className="mt-8 space-y-3">
          <ChoiceRow
            testid="confirm-share-dost-profile"
            title="My Dostination profile"
            subtitle="Always shared. This is how you appear on the platform."
            checked disabled required
          />
          <ChoiceRow
            testid="confirm-share-email"
            title="Email address"
            subtitle={`On file: ${ctx.on_file?.email_masked}`}
            checked={form.share_email} onCheckedChange={(v) => set('share_email')(!!v)}
          />
          {form.share_email ? (
            <Input
              value={form.email_value} onChange={set('email_value')}
              placeholder="you@example.com"
              className="ml-8"
              data-testid="confirm-email-value"
            />
          ) : null}
          <ChoiceRow
            testid="confirm-share-phone"
            title="Phone or text number"
            subtitle={`On file: ${ctx.on_file?.phone_masked}`}
            checked={form.share_phone} onCheckedChange={(v) => set('share_phone')(!!v)}
          />
          {form.share_phone ? (
            <Input
              value={form.phone_value} onChange={set('phone_value')}
              placeholder="+15551234567"
              className="ml-8"
              data-testid="confirm-phone-value"
            />
          ) : null}
          <ChoiceRow
            testid="confirm-share-whatsapp"
            title="WhatsApp number"
            subtitle="Only revealed if you enter one here."
            checked={form.share_whatsapp} onCheckedChange={(v) => set('share_whatsapp')(!!v)}
          />
          {form.share_whatsapp ? (
            <Input
              value={form.whatsapp_value} onChange={set('whatsapp_value')}
              placeholder="+15551234567"
              className="ml-8"
              data-testid="confirm-whatsapp-value"
            />
          ) : null}
          <ChoiceRow
            testid="confirm-share-instagram"
            title="Instagram handle"
            subtitle="Only revealed if you enter one here."
            checked={form.share_instagram} onCheckedChange={(v) => set('share_instagram')(!!v)}
          />
          {form.share_instagram ? (
            <Input
              value={form.instagram_value} onChange={set('instagram_value')}
              placeholder="yourhandle"
              className="ml-8"
              data-testid="confirm-instagram-value"
            />
          ) : null}
          <ChoiceRow
            testid="confirm-keep-inside"
            title="Keep communication inside Dostination only"
            subtitle="No external contact revealed. You can still see each other's profile."
            checked={form.keep_inside_dostination_only}
            onCheckedChange={(v) => set('keep_inside_dostination_only')(!!v)}
          />
        </div>

        <div className="flex flex-wrap gap-2 mt-6">
          <Button
            onClick={() => send(true)} disabled={submitting}
            className="bg-primary-900 text-white hover:bg-primary-800"
            data-testid="confirm-accept-btn"
          >{submitting ? 'Sending...' : 'Accept and share my choices'}</Button>
          <Button
            onClick={() => send(false)} variant="outline" disabled={submitting}
            data-testid="confirm-decline-btn"
          >Politely decline</Button>
        </div>
        {decision?.declined ? (
          <div className="mt-4 text-sm text-neutral-600" data-testid="confirmation-declined-note">
            Thanks for letting us know. This introduction will not be moving forward.
          </div>
        ) : null}
        {decision?.waiting_on_other_party ? (
          <div className="mt-4 text-sm text-emerald-800" data-testid="confirmation-waiting-note">
            <ShieldCheck className="h-4 w-4 inline mr-1" /> Saved. Waiting on the other person.
          </div>
        ) : null}
      </div>
    </div>
  );
};


const ChoiceRow = ({ testid, title, subtitle, checked, onCheckedChange, disabled, required }) => (
  <div className="flex items-start gap-3 border border-neutral-200 rounded-md p-3 bg-white" data-testid={testid}>
    <Checkbox checked={!!checked} onCheckedChange={onCheckedChange} disabled={disabled} className="mt-0.5" />
    <div>
      <div className="text-sm font-medium text-primary-900">
        {title}
        {required ? <span className="ml-1 text-[10px] uppercase text-secondary-800 tracking-widest">required</span> : null}
      </div>
      <div className="text-xs text-neutral-600 mt-0.5">{subtitle}</div>
    </div>
  </div>
);

export default IntroductionConfirmPage;
